import { useLayoutEffect, useMemo, useRef } from 'react';
import type { PricingRecalcOutput } from './hooks/usePricingRecalc';
import type { LoanPricingItem } from '@/services/loanPricingService';
import type { UsePricingDraftOutput, NumericPricingFieldId } from './hooks/usePricingDraft';
import type { BpcCap } from './hooks/useBpcCap';
import type { LocrEligibility } from './hooks/useLocrEligibility';
import { formatCurrency, formatNumber } from './format';
import { formatDollarInputValueWithCaret } from './inputFormat';
import { toNumberOrNull } from '../utils/loanValueHelpers';

interface PricingControlsAsideProps {
  recalc: PricingRecalcOutput;
  pricingItems: LoanPricingItem[];
  /** Round 2A draft state. Inputs are controlled via draft.setValue. */
  draft: UsePricingDraftOutput;
  /** RESPA / plan-cap gate for BPC. */
  bpcCap: BpcCap;
  /** LOCR gate — disabled when no "LO Compensation" ProfitMargin is on the loan. */
  locrEligibility: LocrEligibility;
  /** When true, the aside still renders but all inputs are disabled (snapshot view). */
  isViewingSnapshot: boolean;
  /**
   * Canonical built-in LO Compensation in basis points. Source of truth is
   * `extractEncompassLoCompBps(loan.rate_lock_buy_side_adjustments)` in the
   * workspace; the aside must NOT re-derive from `pricingItems.amount` because
   * that row can diverge from the validator's cap (QA Follow-up #5 Fix 2).
   * Drives LOCR meta Base/Released/Remaining AND the over-cap warning copy.
   */
  builtInLoCompBps: number;
  /**
   * Net-to-Lendwise under the authoritative baseline recalc for "Δ vs. saved".
   * Must NOT include active draft or pending submitted overrides.
   */
  baselineNetToLendwiseDollars: number;
  /**
   * Set while the displayed pricing is below par (par guard on). Renders the
   * warning block at the top of the card; null hides it.
   */
  parShortfall?: { points: number; dollars: number } | null;
  /**
   * True when Set Active is the pending action for the displayed lock
   * (candidate or live mode); false when the lock is already active.
   */
  setActivePending?: boolean;
}

export const PAR_SHORTFALL_NOTICE_ID = 'pv2-par-shortfall';

function ParShortfallNotice({
  points,
  dollars,
  setActivePending,
  locrAvailable
}: {
  points: number;
  dollars: number;
  setActivePending: boolean;
  locrAvailable: boolean;
}) {
  // Name only remedies that are live on this lock: no LO comp line means no
  // LO comp reduction to offer.
  const remedies = locrAvailable
    ? 'discount points, an LO comp reduction, or a concession'
    : 'discount points or a concession';
  const body = setActivePending
    ? `This lock can't be set active until the shortfall is covered. Add ${remedies}.`
    : `Cover the shortfall with ${remedies}.`;
  return (
    <div id={PAR_SHORTFALL_NOTICE_ID} className="aside-warn par" role="status">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
      <span className="msg">
        <span className="lead">
          Below par by {points.toFixed(3)} pts (−{formatCurrency(dollars)})
        </span>
        {body}
      </span>
    </div>
  );
}

/**
 * Right sticky Pricing Controls aside. Inputs are controlled via `draft`
 * (see Phase 2 plan §2A.2). BPC field is additionally locked by `bpcCap`.
 */
export function PricingControlsAside({
  recalc,
  pricingItems: _pricingItems,
  draft,
  bpcCap,
  locrEligibility,
  isViewingSnapshot,
  builtInLoCompBps,
  baselineNetToLendwiseDollars,
  parShortfall = null,
  setActivePending = false
}: PricingControlsAsideProps) {
  const showParShortfall = parShortfall !== null && !isViewingSnapshot;
  // The Investor Loan # lives in Lock Details; only these four inputs make the card unsaved.
  const pricingInputsDirty = draft.dirtyFields.some((id) => id !== 'investor_loan_number');
  const parDescribedBy = showParShortfall ? PAR_SHORTFALL_NOTICE_ID : undefined;
  const discountField = draft.values.discount_point_percent;
  const bpcField = draft.values.origination_fee_percent;
  const lenderCreditField = draft.values.lender_credit_4794_dollars;
  const locrField = draft.values.lo_comp_reduction_bps;
  const lenderCreditInputRef = useRef<HTMLInputElement | null>(null);
  const lenderCreditCaretRef = useRef<number | null>(null);

  const discountNum = discountField.value === '' ? null : toNumberOrNull(discountField.value);
  const bpcPercentNum = bpcField.value === '' ? null : toNumberOrNull(bpcField.value);
  const lenderPaidClosingCostDollars = recalc.lenderPaidClosingCostDollars; // LENPCC read-only aggregation

  // Dollar-value parser for the lender credit input. Strips commas + whitespace
  // before Number() so "1,500.00" parses to 1500. Blank → 0 so headroom reads
  // as the full cap when the user hasn't typed anything yet.
  const parseLenderCreditDollars = (raw: string): number => {
    const cleaned = raw.replace(/[,\s]/g, '');
    if (cleaned === '') {
      return 0;
    }
    const n = Number(cleaned);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  };

  const discountDollars = useMemo(
    () => (discountNum !== null ? discountNum * recalc.loanAmount * 0.01 : null),
    [discountNum, recalc.loanAmount]
  );
  const bpcDollars = useMemo(
    () => (bpcPercentNum !== null ? (bpcPercentNum / 100) * recalc.loanAmount : null),
    [bpcPercentNum, recalc.loanAmount]
  );

  // Lender credit cap math (shared with server-side validateLenderCreditCap).
  // `availableLcPts` is the total lender-credit headroom in points given the
  // current waterfall (includes staged Discount + LOCR via draft.recalcOverrides).
  // `maxTotalLenderCreditDollars` is the dollars-equivalent of that headroom.
  // `maxGenericLenderCreditDollars` subtracts the already-itemized lender credit
  // (LENPCC) so the user's editable generic LC input can't push the total past
  // available. Value = 0 floor (never negative).
  const maxTotalLenderCreditDollars = Math.max(
    0,
    recalc.availableLcPts * recalc.loanAmount * 0.01
  );
  const maxGenericLenderCreditDollars = Math.max(
    0,
    maxTotalLenderCreditDollars - recalc.lenderPaidClosingCostDollars
  );
  const lenderCreditPts = recalc.lenderCreditPoints;

  // Dynamic headroom = cap − what the user has typed. When the typed value
  // exceeds the cap, headroom floors at $0 (matches the spec screenshot) so
  // the user sees the available-ness of the field reach zero exactly when
  // the warning box appears.
  const currentGenericLcDollars = parseLenderCreditDollars(lenderCreditField.value);
  const lenderCreditHeadroomDollars = Math.max(
    0,
    maxGenericLenderCreditDollars - currentGenericLcDollars
  );
  const lenderCreditInvalid = lenderCreditField.invalid !== null;

  // LOCR cap/meta source is the canonical builtInLoCompBps prop (QA #5 Fix 2),
  // not a re-derivation from loan_pricing_items. Using the prop keeps the
  // warning ceiling, the meta row, and the server validator on one number.
  const locrBpsNum = locrField.value === '' ? 0 : (toNumberOrNull(locrField.value) ?? 0);
  const releasedBps = Math.min(locrBpsNum, builtInLoCompBps);
  const remainingBps = Math.max(0, builtInLoCompBps - releasedBps);
  const locrInvalid = locrField.invalid !== null;
  // Respect fractional bps (e.g. `50.50` released) in the meta row + warning
  // copy. Any fractional among base/released/remaining flips to 2 decimals.
  const locrMetaDecimals = [builtInLoCompBps, releasedBps, remainingBps].some(
    (v) => Math.abs(v - Math.round(v)) > 0.0001
  )
    ? 2
    : 0;

  const bonaFideLabel = recalc.bonaFide === 'Y' ? 'Bona-fide · Y' : recalc.bonaFide === 'N' ? 'Bona-fide · N' : 'Bona-fide · —';

  // BPC input is additionally locked when RESPA/no-plan blocks editing.
  const bpcDisabled = isViewingSnapshot || !bpcCap.allowEdit;
  // Non-BPC inputs are only locked in snapshot mode.
  const genericDisabled = isViewingSnapshot;
  // LOCR gets an additional gate: no "LO Compensation" in the price → disabled.
  const locrDisabled = genericDisabled || !locrEligibility.allowEdit;

  // Phase 2 QA Fix 2 + follow-up — field-specific input sanitizer. No letters
  // anywhere; no negatives at entry; no dollar signs / whitespace in numeric
  // fields; at most one decimal point; per-field fractional-digit cap applied
  // via truncation (not rounding) at entry. Cap-VALUE violations (over
  // maxGenericLenderCreditDollars / builtInLoCompBps) are NOT clamped here —
  // the workspace effect flags them via draft.setInvalid → Save disabled, and
  // the server re-validates before any DB mutation.
  type FieldKind = 'discount_pct' | 'bpc_pct' | 'lender_credit_dollars' | 'locr_bps';

  const KIND_BY_ID: Record<NumericPricingFieldId, FieldKind> = {
    discount_point_percent: 'discount_pct',
    origination_fee_percent: 'bpc_pct',
    lender_credit_4794_dollars: 'lender_credit_dollars',
    lo_comp_reduction_bps: 'locr_bps'
  };

  /** Collapse all decimal points after the first; safe for '1.2.3.4' → '1.234'. */
  const oneDot = (s: string): string => {
    const [head, ...rest] = s.split('.');
    return rest.length ? `${head}.${rest.join('')}` : head;
  };

  /**
   * Cap fractional digits via truncation so `.685656556` → `.685` (not `.686`).
   * Rounding would silently alter user intent; truncation mirrors what the
   * server's numeric cast would yield for input beyond precision. A lone
   * trailing dot (`"1."`) is preserved mid-type so the decimal key isn't eaten.
   */
  const limitDecimals = (s: string, maxDecimals: number): string => {
    const dot = s.indexOf('.');
    if (dot === -1) {
      return s;
    }
    return s.slice(0, dot + 1 + maxDecimals);
  };

  const sanitizeNumericInput = (raw: string, kind: FieldKind): string => {
    switch (kind) {
      case 'discount_pct':
        return limitDecimals(oneDot(raw.replace(/[^0-9.]/g, '')), 3);
      case 'bpc_pct':
        return limitDecimals(oneDot(raw.replace(/[^0-9.]/g, '')), 3);
      case 'lender_credit_dollars':
        return limitDecimals(oneDot(raw.replace(/[^0-9.,]/g, '')), 2);
      case 'locr_bps':
        // LOCR now accepts decimals (valid inputs like `50.50` bps); capped at
        // 2 fractional digits. Whole-integer bps still work unchanged.
        return limitDecimals(oneDot(raw.replace(/[^0-9.]/g, '')), 2);
    }
  };

  useLayoutEffect(() => {
    const caret = lenderCreditCaretRef.current;
    const input = lenderCreditInputRef.current;
    if (caret === null || input === null || document.activeElement !== input) {
      lenderCreditCaretRef.current = null;
      return;
    }
    input.setSelectionRange(caret, caret);
    lenderCreditCaretRef.current = null;
  }, [lenderCreditField.value]);

  const onChange = (id: NumericPricingFieldId) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (id === 'lender_credit_4794_dollars') {
      const raw = e.target.value;
      const { value, caret } = formatDollarInputValueWithCaret(
        raw,
        e.currentTarget.selectionStart ?? raw.length
      );
      lenderCreditCaretRef.current = caret;
      draft.setValue(id, value);
      return;
    }
    draft.setValue(id, sanitizeNumericInput(e.target.value, KIND_BY_ID[id]));
  };

  // Paste handler preserves existing text + selection (don't wipe the input on paste).
  const onPaste = (id: NumericPricingFieldId, kind: FieldKind) => (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const input = e.currentTarget;
    const pasted = e.clipboardData.getData('text');
    const selStart = input.selectionStart ?? input.value.length;
    const selEnd = input.selectionEnd ?? input.value.length;
    const next = input.value.slice(0, selStart) + pasted + input.value.slice(selEnd);
    if (id === 'lender_credit_4794_dollars') {
      const { value, caret } = formatDollarInputValueWithCaret(next, selStart + pasted.length);
      lenderCreditCaretRef.current = caret;
      draft.setValue(id, value);
      return;
    }
    draft.setValue(id, sanitizeNumericInput(next, kind));
  };

  const maxBpcLabel = bpcCap.capPercent !== null
    ? `Max BPC plan ${bpcCap.capPercent.toFixed(2)}%`
    : 'Max BPC plan —';

  // Footer — Projected net uses the active recalc. Δ vs. saved compares it to
  // the authoritative baseline; signedCurrency renders ±$0 exactly at zero.
  const projectedNet = recalc.netToLendwiseDollars;
  const deltaVsSaved = projectedNet - baselineNetToLendwiseDollars;
  const deltaClass = deltaVsSaved > 0.005 ? 'pos' : deltaVsSaved < -0.005 ? 'neg' : 'zero';
  const signedCurrency = (n: number): string => {
    if (Math.abs(n) < 0.005) {
      return '±$0';
    }
    return (n > 0 ? '+' : '−') + formatCurrency(Math.abs(n));
  };

  return (
    <aside>
      <div className="aside-hdr">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
          <path d="M4 6h16M7 12h10M10 18h4" />
        </svg>
        <h3>Pricing controls</h3>
        {!isViewingSnapshot && pricingInputsDirty && (
          <span className="dirty-chip">
            <span className="dot" aria-hidden />
            unsaved
          </span>
        )}
      </div>

      {showParShortfall && (
        <ParShortfallNotice
          points={parShortfall.points}
          dollars={parShortfall.dollars}
          setActivePending={setActivePending}
          locrAvailable={locrEligibility.allowEdit}
        />
      )}

      <div className="aside-section-label">Price inputs</div>

      <div className="aside-field">
        <div className="aside-field-hdr">
          <span className="lbl" id="pv2-lbl-discount">Discount points</span>
          <span className="hint">borrower pays</span>
        </div>
        <div className="aside-input-wrap">
          <input
            type="text"
            inputMode="decimal"
            value={discountField.value}
            onChange={onChange('discount_point_percent')}
            onPaste={onPaste('discount_point_percent', 'discount_pct')}
            disabled={genericDisabled}
            aria-invalid={discountField.invalid !== null}
            aria-labelledby="pv2-lbl-discount"
            aria-describedby={parDescribedBy}
          />
          <span className="suffix">%</span>
        </div>
        <div className="aside-field-footer">
          <span>≈ {discountDollars !== null ? formatCurrency(discountDollars) : '—'}</span>
          <span>{bonaFideLabel}</span>
        </div>
        {discountField.invalid && (
          <div className="aside-field-note" role="alert">{discountField.invalid}</div>
        )}
      </div>

      <div className="aside-field">
        <div className="aside-field-hdr">
          <span className="lbl" id="pv2-lbl-bpc">Borrower-paid comp</span>
          <span className="hint">percent</span>
        </div>
        <div className="aside-input-wrap">
          <input
            type="text"
            inputMode="decimal"
            value={bpcField.value}
            onChange={onChange('origination_fee_percent')}
            onPaste={onPaste('origination_fee_percent', 'bpc_pct')}
            disabled={bpcDisabled}
            aria-invalid={bpcField.invalid !== null}
            aria-labelledby="pv2-lbl-bpc"
            title={!bpcCap.allowEdit ? bpcCap.reason : undefined}
          />
          <span className="suffix">%</span>
        </div>
        <div className="aside-field-footer">
          <span>≈ {bpcDollars !== null ? formatCurrency(bpcDollars) : '—'}</span>
          <span>{maxBpcLabel}</span>
        </div>
        {bpcField.invalid && (
          <div className="aside-field-note" role="alert">{bpcField.invalid}</div>
        )}
        {!bpcCap.allowEdit && !bpcField.invalid && (
          <div className="aside-field-note">{bpcCap.reason}</div>
        )}
      </div>

      <div className="aside-section-label">Givebacks</div>

      <div className="aside-field">
        <div className="aside-field-hdr">
          <span className="lbl" id="pv2-lbl-lender-credit">
            Lender credit
            <svg
              className="info-icon"
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          </span>
          <span className="hint">generic lender credit</span>
        </div>
        <div className={`aside-input-wrap has-prefix${lenderCreditInvalid ? ' error' : ''}`}>
          <span className="prefix" aria-hidden>$</span>
          <input
            ref={lenderCreditInputRef}
            type="text"
            inputMode="decimal"
            value={lenderCreditField.value}
            onChange={onChange('lender_credit_4794_dollars')}
            onPaste={onPaste('lender_credit_4794_dollars', 'lender_credit_dollars')}
            disabled={genericDisabled}
            aria-invalid={lenderCreditInvalid}
            aria-labelledby="pv2-lbl-lender-credit"
          />
        </div>
        <div className="aside-field-footer">
          <span>{formatNumber(lenderCreditPts, 3)} pts</span>
          <span>Headroom {formatCurrency(lenderCreditHeadroomDollars)}</span>
        </div>
        {lenderPaidClosingCostDollars > 0 && (
          <div className="aside-field-note">
            + {formatCurrency(lenderPaidClosingCostDollars)} already itemized Lender Credit
          </div>
        )}
        {lenderCreditInvalid && (
          <div className="aside-warn" role="alert">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <span className="msg">
              Exceeds available lender credit. Reduce to {formatCurrency(maxGenericLenderCreditDollars)} to save.
            </span>
          </div>
        )}
      </div>

      <div className="aside-field">
        <div className="aside-field-hdr">
          <span className="lbl" id="pv2-lbl-locr">LO comp reduction</span>
          <span className="hint">basis points</span>
        </div>
        <div className={`aside-input-wrap${locrInvalid ? ' error' : ''}`}>
          <input
            type="text"
            inputMode="decimal"
            value={locrField.value}
            onChange={onChange('lo_comp_reduction_bps')}
            onPaste={onPaste('lo_comp_reduction_bps', 'locr_bps')}
            disabled={locrDisabled}
            aria-invalid={locrInvalid}
            aria-labelledby="pv2-lbl-locr"
            aria-describedby={parDescribedBy}
            title={!locrEligibility.allowEdit ? locrEligibility.reason ?? undefined : undefined}
          />
          <span className="suffix">bps</span>
        </div>
        {!locrEligibility.allowEdit && locrEligibility.reason && (
          <div className="aside-field-note">{locrEligibility.reason}</div>
        )}
        <div className="aside-field-footer">
          <span>≈ {formatCurrency((locrBpsNum / 10000) * recalc.loanAmount)}</span>
          <span>
            Base {formatNumber(builtInLoCompBps, locrMetaDecimals)} · Released {formatNumber(releasedBps, locrMetaDecimals)} · Remaining {formatNumber(remainingBps, locrMetaDecimals)}
          </span>
        </div>
        {locrInvalid && (
          <div className="aside-warn" role="alert">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <span className="msg">
              Exceeds built-in LO compensation. Reduce to {formatNumber(builtInLoCompBps, locrMetaDecimals)} bps to save.
            </span>
          </div>
        )}
      </div>

      <div className="aside-footer">
        <div className="summary">
          <span>Projected net</span>
          <span className={`v ${projectedNet >= 0 ? 'pos' : 'neg'}`}>
            {formatCurrency(projectedNet)}
          </span>
        </div>
        <div className="summary">
          <span>Δ vs. saved</span>
          <span className={`v ${deltaClass}`}>{signedCurrency(deltaVsSaved)}</span>
        </div>
      </div>
    </aside>
  );
}
